import { I18nProvider } from '@lingui/react';
import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  ALL_LOCALES,
  type AppLocale,
  LOCALE_INFO,
  type LocaleInfo,
  PSEUDO_LOCALE,
} from './locales';
import { activateLocale, i18n } from './runtime';

export interface LocaleContextValue {
  locale: AppLocale;
  /** What a language picker should offer: the shipped packs, plus the pseudo-locale when allowed. */
  options: readonly LocaleInfo[];
  allowPseudo: boolean;
  /** Ask the host to switch language; it persists the choice and re-renders with a new `locale`. */
  setLocale: (locale: AppLocale) => void;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export interface LocaleProviderProps {
  /** Resolved by the host from its own sources (device, URL, stored choice) with `resolveLocale`. */
  locale: AppLocale;
  onLocaleChange?: (locale: AppLocale) => void;
  /** Offer `en-XA` in pickers — non-production builds only. */
  allowPseudo?: boolean;
  children: ReactNode;
}

/**
 * Mount once, outside everything that renders text. The catalog for `locale` is activated
 * before the first child renders (so even the first frame is translated) and again whenever
 * it changes; Lingui's I18nProvider then re-renders every <Trans> and `t` consumer.
 */
export function LocaleProvider({
  locale,
  onLocaleChange,
  allowPseudo = false,
  children,
}: LocaleProviderProps) {
  // A lazy initialiser runs exactly once, before children mount — the one place a
  // synchronous activation can happen without updating subscribers mid-render.
  useState(() => activateLocale(locale));
  useLayoutEffect(() => activateLocale(locale), [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      allowPseudo,
      options: ALL_LOCALES.filter((tag) => allowPseudo || tag !== PSEUDO_LOCALE).map(
        (tag) => LOCALE_INFO[tag],
      ),
      setLocale: (next) => onLocaleChange?.(next),
    }),
    [locale, allowPseudo, onLocaleChange],
  );

  return (
    <LocaleContext.Provider value={value}>
      <I18nProvider i18n={i18n}>{children}</I18nProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('useLocale must be used inside <LocaleProvider>');
  return context;
}
